import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Flame, FileQuestion, Clock, Timer, Trophy } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface GoalStreak {
  goal_type: string;
  current_streak: number;
  longest_streak: number;
}

interface NextAchievement {
  name: string;
  requirement_value: number;
  points_reward: number;
  rarity: string;
}

const GOAL_CONFIG = {
  questions: {
    label: 'Questões',
    icon: FileQuestion,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    achievementType: 'questions_goal_streak',
  },
  study_time: {
    label: 'Tempo',
    icon: Clock,
    color: 'text-green-500',
    bgColor: 'bg-green-500/10',
    achievementType: 'time_goal_streak',
  },
  pomodoros: {
    label: 'Pomodoros',
    icon: Timer,
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/10',
    achievementType: 'pomodoro_goal_streak',
  },
};

const RARITY_COLORS = {
  common: 'text-muted-foreground',
  rare: 'text-blue-500',
  epic: 'text-purple-500',
  legendary: 'text-amber-500',
};

export function GoalStreakWidget() {
  const { user } = useAuth();

  const { data: streaks, isLoading: streaksLoading } = useQuery({
    queryKey: ['goal-streaks', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_goal_streaks')
        .select('goal_type, current_streak, longest_streak')
        .eq('user_id', user!.id);
      if (error) throw error;
      return data as GoalStreak[];
    },
    enabled: !!user,
  });

  const { data: achievements } = useQuery({
    queryKey: ['goal-streak-achievements'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('achievements')
        .select('id, name, requirement_type, requirement_value, points_reward, rarity')
        .in('requirement_type', ['questions_goal_streak', 'time_goal_streak', 'pomodoro_goal_streak'])
        .order('requirement_value', { ascending: true });
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const { data: userAchievements } = useQuery({
    queryKey: ['user-achievements', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_achievements')
        .select('achievement_id')
        .eq('user_id', user!.id);
      if (error) throw error;
      return new Set(data?.map(a => a.achievement_id));
    },
    enabled: !!user,
  });

  const getNextAchievement = (goalType: string): NextAchievement | null => {
    if (!Array.isArray(achievements) || !userAchievements || !(userAchievements instanceof Set)) return null;
    const config = GOAL_CONFIG[goalType as keyof typeof GOAL_CONFIG];
    if (!config) return null;

    const typeAchievements = achievements.filter(
      a => a.requirement_type === config.achievementType && !userAchievements.has(a.id)
    );
    
    return typeAchievements[0] || null;
  };

  const getStreakValue = (goalType: string): number => {
    return streaks?.find(s => s.goal_type === goalType)?.current_streak || 0;
  };

  if (streaksLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <Skeleton className="h-5 w-40" />
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-24 rounded-lg" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2 p-3 md:p-6 md:pb-2">
        <CardTitle className="text-sm md:text-base flex items-center gap-2">
          <Flame className="h-4 w-4 md:h-5 md:w-5 text-orange-500" />
          Metas Consecutivas
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 md:p-6 pt-0 md:pt-0">
        <div className="grid grid-cols-3 gap-2 sm:gap-3 md:gap-4">
          {Object.entries(GOAL_CONFIG).map(([goalType, config]) => {
            const streak = getStreakValue(goalType);
            const nextAchievement = getNextAchievement(goalType);
            const progress = nextAchievement 
              ? Math.min(100, (streak / nextAchievement.requirement_value) * 100)
              : 100;

            return (
              <div
                key={goalType}
                className={`p-2 sm:p-3 md:p-4 rounded-lg border card-hover-gradient hover-lift ${config.bgColor}`}
              >
                <div className="flex items-center gap-1 md:gap-2 mb-1 md:mb-2">
                  <config.icon className={`h-3 w-3 md:h-4 md:w-4 ${config.color}`} />
                  <span className="font-medium text-[10px] md:text-sm truncate">{config.label}</span>
                </div>
                
                <div className="flex items-baseline gap-0.5 md:gap-1 mb-1 md:mb-2">
                  <Flame className="h-3 w-3 md:h-4 md:w-4 text-orange-500" />
                  <span className="text-lg md:text-2xl font-bold">{streak}</span>
                  <span className="text-[9px] md:text-xs text-muted-foreground">d</span>
                </div>

                {nextAchievement ? (
                  <>
                    <Progress value={progress} className="h-1 md:h-1.5 mb-1 md:mb-2" />
                    <div className="flex items-center justify-between text-[9px] md:text-xs">
                      <span className="text-muted-foreground hidden sm:inline">
                        Próx: {nextAchievement.requirement_value}d
                      </span>
                      <span className={`flex items-center gap-0.5 ${RARITY_COLORS[nextAchievement.rarity as keyof typeof RARITY_COLORS]}`}>
                        <Trophy className="h-2.5 w-2.5 md:h-3 md:w-3" />
                        <span className="hidden sm:inline">+{nextAchievement.points_reward}</span>
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center gap-0.5 text-[9px] md:text-xs text-amber-500">
                    <Trophy className="h-2.5 w-2.5 md:h-3 md:w-3" />
                    <span className="truncate">Completo!</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
